import React, { useState, useCallback, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { AlertCircle, FileX, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import {
  UploadDocumentsWrapper,
  UploadDocumentsContainer,
  HeaderSection,
  SectionTitle,
  SectionDescription,
  ActionButtons,
  DashboardButton,
  SaveButton,
  ContentGrid,
  // DocumentDetailsSection,
  UploadSection,
  SectionLabel,
  // FormGroup,
  // InputLabel,
  // TextInput,
  // TextAreaInput,
  // CharacterCount,
  ErrorText,
  UploadedDocumentsSection,
  DocumentsList,
  EmptyState,
  GlobalError,
  UploadedDocumentsHeaderRow,
} from './uploadDocuments.styles';

import DropZone from './components/Dropzone';
import DocumentListItem from './components/Documentlistitem';
import FolderProgressBar from './components/FolderProgressBar';

import {
  useNewSaveDocument,
  useSaveDocumentList,
  useDeleteDocument,
  useViewDocument,
} from './hooks/Useuploaddocuments';
import useUploadQueue from './hooks/useUploadQueue';

import {
  //  validateUploadForm,
  formatFileSize,
  formatFileSizeFromBytes,
} from './utils';
import UploadSuccessModal from './modals/UploadSuccessModal';
// import { MAX_FILE_SIZE } from '../../settings/constant';
import { useAuth } from '../../hooks/useAuth';

// const MAX_DESCRIPTION_LENGTH = 500;

// The API only accepts up to this many pdfArray entries per save call —
// larger successful-upload sets are split into sequential chunks of this
// size (see handleSave).
const SAVE_BATCH_SIZE = 30;

const createFolderName = () => uuidv4().replace(/-/g, '');

const generateSixDigitCode = () => Math.floor(100000 + Math.random() * 900000).toString();

const buildDocumentPath = (folderPath, fileName) =>
  folderPath ? `${folderPath}/${fileName}` : fileName;

// Strips the extension from a file name, e.g. "pdf.pdf" -> "pdf".
const stripExtension = (fileName) => fileName.replace(/\.[^/.]+$/, '');

// Splits an array into consecutive chunks of at most `size` items each,
// e.g. chunkArray([1..70], 50) -> [[1..50], [51..70]].
const chunkArray = (items, size) => {
  const chunks = [];
  for (let i = 0; i < items.length; i += size) {
    chunks.push(items.slice(i, i + size));
  }
  return chunks;
};

// Used only for REAL folder uploads (relativePath is populated) — either
// the "Choose Folder" input (webkitdirectory) or a folder dropped onto the
// zone. relativePath is the folder chain, e.g. "folder1/subfolder".
// Only the top-level segment is needed here (for rootFolder/folderName
// grouping) — documentPath itself keeps using the full relativePath,
// unchanged from the existing logic.
const resolveFolderUploadRoot = (relativePath) => relativePath.split('/').filter(Boolean)[0];

const UploadDocuments = () => {
  const navigate = useNavigate();

  // Maps each uploaded root folder name -> its generated folderName (uuid).
  // A "root folder" can be:
  //   - a real folder the user dragged/selected in (top-level folder name)
  //   - a single dropped file's own name (no extension) — one root per file
  //   - a shared pseudo-folder "Folder_XXXXXX" for a whole "Browse Files"
  //     selection, so those files land together in the backend
  // const [folderMap, setFolderMap] = useState({});
  const folderMapRef = useRef({});

  // const [title, setTitle] = useState('');
  // const [description, setDescription] = useState('');

  // Each item: {
  //   id (Uppy file id), file, name, size (display string), sizeBytes,
  //   fileType, rootFolder, folderName, documentPath, isFolderUpload,
  //   status: 'pending'|'uploading'|'done'|'error'|'size-error'|'skipped',
  //   documentId, errorMessage
  // }
  const [documents, setDocuments] = useState([]);

  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [docLoadingState, setDocLoadingState] = useState({});

  // True for the entire duration of the (possibly multi-batch) save
  // operation kicked off by handleSave — spans every sequential
  // saveList call, not just the currently in-flight one.
  const [isSavingAll, setIsSavingAll] = useState(false);

  const { registerFiles, clear: clearUploadQueue } = useUploadQueue();
  const { councilInfo } = useAuth();
  const MAX_FILE_SIZE = councilInfo?.MAX_FILE_SIZE || 50 * 1024 * 1024; // Default to 50MB if not set

  // Returns the folderName (uuid) for a given root folder, generating and
  // registering a new one the first time that root folder is seen.
  const getOrCreateFolderName = useCallback((rootFolder) => {
    if (!folderMapRef.current[rootFolder]) {
      folderMapRef.current = {
        ...folderMapRef.current,
        [rootFolder]: createFolderName(),
      };
      // setFolderMap(folderMapRef.current);
    }
    return folderMapRef.current[rootFolder];
  }, []);

  // Generates a "Folder_XXXXXX" pseudo root that isn't already in use.
  const createUniquePseudoRoot = useCallback(() => {
    let pseudoRoot = `Folder_${generateSixDigitCode()}`;
    while (folderMapRef.current[pseudoRoot]) {
      pseudoRoot = `Folder_${generateSixDigitCode()}`;
    }
    return pseudoRoot;
  }, []);

  // ─── Derived state ──────────────────────────────────────────────────────────

  const isUploadingAny = documents.some((d) => d.status === 'uploading');
  const hasSuccessfulUploads = documents.some((d) => d.status === 'done');
  const hasPendingOrUploading = documents.some(
    (d) => d.status === 'pending' || d.status === 'uploading'
  );
  // const hasSkippedOrSizeError = documents.some(
  //   (d) => d.status === 'skipped' || d.status === 'size-error'
  // );
  const buttonsDisabled = isUploadingAny;

  // Groups documents that came from a REAL folder upload (webkitdirectory or
  // a dropped folder) by rootFolder, so we can show one aggregate progress
  // bar per folder. "Browse Files" pseudo-folders and lone dropped files are
  // intentionally excluded — this is specifically for folder uploads.
  const folderGroups = React.useMemo(() => {
    const map = {};
    documents.forEach((d) => {
      if (!d.isFolderUpload) return;
      if (!map[d.rootFolder]) map[d.rootFolder] = [];
      map[d.rootFolder].push(d);
    });
    return map;
  }, [documents]);

  // ─── Mutations ──────────────────────────────────────────────────────────────
  // NOTE: folderName is now per-document (each root folder gets its own
  // generated folderName), so it's no longer bound at hook-creation time.
  // It's passed per-call instead, alongside the other mutate variables.
  // The corresponding hooks in `./hooks/Useuploaddocuments` need to accept
  // `folderName` from the mutate variables rather than as a hook argument.

  const { mutate: uploadDocument } = useNewSaveDocument();

  const { mutate: deleteDoc } = useDeleteDocument({
    onSuccess: (_, { documentId }) => {
      setDocuments((prev) => prev.filter((d) => d.documentId !== documentId));
    },
    onError: (err) => {
      setGlobalError(err?.message || 'Failed to delete document. Please try again.');
    },
  });

  const { mutate: viewDoc } = useViewDocument({
    onSuccess: (data) => {
      const url = data?.documentUrl ?? data?.url;
      if (url) window.open(url, '_blank');
    },
    onError: (err) => {
      setGlobalError(err?.message || 'Failed to open document. Please try again.');
    },
  });

  // Success/error handling for the save flow is done per-call inside
  // handleSave (see runBatch below) rather than here, since a single Save
  // click may fire several sequential saveList calls — the modal should
  // only appear once, after the LAST batch succeeds.
  const { mutate: saveList } = useSaveDocumentList();

  // ─── Sequential upload engine ───────────────────────────────────────────────

  const startUpload = useCallback(
    (doc) => {
      setDocuments((prev) =>
        prev.map((d) =>
          d.id === doc.id ? { ...d, status: 'uploading', errorMessage: '', progress: 0 } : d
        )
      );

      uploadDocument(
        {
          file: doc.file,
          documentPath: doc.documentPath,
          folderName: doc.folderName,
          isExisting: false,
          onUploadProgress: (percent) => {
            setDocuments((prev) =>
              prev.map((d) => (d.id === doc.id ? { ...d, progress: percent } : d))
            );
          },
        },
        {
          onSuccess: (data) => {
            setDocuments((prev) =>
              prev.map((d) =>
                d.id === doc.id
                  ? { ...d, status: 'done', progress: 100, documentId: data.documentId }
                  : d
              )
            );
          },
          onError: (err) => {
            setDocuments((prev) =>
              prev.map((d) =>
                d.id === doc.id
                  ? {
                      ...d,
                      status: 'error',
                      progress: 0,
                      errorMessage:
                        err?.response?.data?.message ||
                        err?.message ||
                        'Upload failed. Please retry.',
                    }
                  : d
              )
            );
          },
        }
      );
    },
    [uploadDocument]
  );

  useEffect(() => {
    if (documents.length === 0) return;
    if (isUploadingAny) return;

    const nextPending = documents.find((d) => d.status === 'pending');
    if (nextPending) {
      startUpload(nextPending);
    }
  }, [documents, isUploadingAny, startUpload]);

  // ─── Handlers ───────────────────────────────────────────────────────────────

  // `source` (passed up from DropZone) tells us HOW the files arrived, since
  // relativePath alone can't distinguish "Browse Files" from files dropped
  // in individually — both produce an empty relativePath per entry.
  const handleFilesSelect = useCallback(
    (fileEntries, source) => {
      if (fileEntries.length === 0) return;

      const registered = registerFiles(fileEntries);

      // "Browse Files" groups the WHOLE batch from this one selection under
      // a single pseudo root folder, generated once per call (not per
      // file), so all files picked in that dialog land together.
      const browseFilesRoot = source === 'browse-files' ? createUniquePseudoRoot() : null;

      const queue = registered.map(({ id, file, relativePath }) => {
        let rootFolder;
        let documentPath;
        // Only a real folder upload (webkitdirectory input or a folder
        // dropped onto the zone) gets an aggregate folder progress bar —
        // "Browse Files" and lone dropped files do not, even though they
        // also get grouped under a rootFolder for backend purposes.
        const isFolderUpload = Boolean(relativePath);

        if (relativePath) {
          // ── Case 1: real folder upload ──────────────────────────────────
          // Either the "Choose Folder" input (webkitdirectory) or a folder
          // dropped onto the zone (including multiple folders dropped in
          // the same batch — each file resolves its own root independently
          // here since this is a per-file map). Root = top-level folder
          // name (only used for folderName/folderArray grouping);
          // documentPath keeps using the FULL relativePath + fileName,
          // exactly as before — unchanged.
          rootFolder = resolveFolderUploadRoot(relativePath);
          documentPath = buildDocumentPath(relativePath, file.name);
        } else if (source === 'browse-files') {
          // ── Case 2: "Browse Files" button ───────────────────────────────
          // Every file in this selection shares one pseudo root so the
          // backend groups them together.
          rootFolder = browseFilesRoot;
          documentPath = buildDocumentPath(rootFolder, file.name);
        } else {
          // ── Case 3: individual file(s) dropped directly (no folder) ────
          // Each file is its own root, and documentPath is prefixed with
          // that root ("rootFolder/fileName") so the backend can match
          // folderArray entries to pdfArray entries.
          rootFolder = stripExtension(file.name);
          documentPath = buildDocumentPath(rootFolder, file.name);
        }

        const folderName = getOrCreateFolderName(rootFolder);

        const sizeInBytes = file.size;
        const isSizeExceeded = sizeInBytes > MAX_FILE_SIZE;

        return {
          id,
          file,
          name: file.name,
          size: formatFileSize(sizeInBytes),
          sizeBytes: sizeInBytes,
          fileType: file.type,
          rootFolder,
          folderName,
          documentPath,
          isFolderUpload,
          status: isSizeExceeded ? 'size-error' : 'pending',
          progress: 0,
          documentId: null,
          errorMessage: isSizeExceeded
            ? `File exceeds ${formatFileSizeFromBytes(MAX_FILE_SIZE)} limit. Please choose a smaller file.`
            : '',
        };
      });

      setErrors((prev) => ({ ...prev, documents: undefined }));
      setGlobalError('');
      setDocuments((prev) => [...prev, ...queue]);
    },
    [registerFiles, getOrCreateFolderName, createUniquePseudoRoot]
  );

  const handleReupload = useCallback(
    (doc) => {
      setGlobalError('');
      startUpload(doc);
    },
    [startUpload]
  );

  const handleSkip = useCallback((doc) => {
    setGlobalError('');
    setDocuments((prev) => prev.map((d) => (d.id === doc.id ? { ...d, status: 'skipped' } : d)));
  }, []);

  const handleView = useCallback(
    (doc) => {
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'viewing' }));
      setGlobalError('');
      viewDoc(
        { documentId: doc.documentId, applicationId: doc.folderName },
        {
          onSettled: () => {
            setDocLoadingState((prev) => {
              const next = { ...prev };
              delete next[doc.id];
              return next;
            });
          },
        }
      );
    },
    [viewDoc]
  );

  const handleDelete = useCallback(
    (doc) => {
      const confirmed = window.confirm('Are you sure you want to remove this document?');
      if (!confirmed) return;
      setDocLoadingState((prev) => ({ ...prev, [doc.id]: 'deleting' }));
      setGlobalError('');
      deleteDoc(
        { documentId: doc.documentId, applicationId: doc.folderName },
        {
          onSettled: () => {
            setDocLoadingState((prev) => {
              const next = { ...prev };
              delete next[doc.id];
              return next;
            });
          },
        }
      );
    },
    [deleteDoc]
  );

  const handleSave = () => {
    if (isSavingAll) return;

    setGlobalError('');

    // Only validate documents that are successfully uploaded
    const successfulDocs = documents.filter((d) => d.status === 'done');

    if (successfulDocs.length === 0) {
      setGlobalError('Please upload at least one successful document before saving.');
      return;
    }

    // const { isValid, errors: validationErrors } = validateUploadForm({
    //   title,
    //   documents: successfulDocs,
    // });

    // const descriptionError = !description.trim() ? 'Description is required' : undefined;
    // const mergedErrors = { ...validationErrors, description: descriptionError };
    // setErrors(mergedErrors);

    // if (!isValid || descriptionError) return;

    if (hasPendingOrUploading) {
      setGlobalError('Please wait for all uploads to finish (or skip failed files) before saving.');
      return;
    }

    // Build one folderArray entry per distinct root folder represented among
    // the successfully-uploaded documents. Sent with every batch below so
    // the backend has the full folder set regardless of which batch is in
    // flight.
    const seenRootFolders = new Set();
    const folderArray = [];
    successfulDocs.forEach((d) => {
      if (!seenRootFolders.has(d.rootFolder)) {
        seenRootFolders.add(d.rootFolder);
        folderArray.push({
          folderName: d.folderName,
          rootFolder: d.rootFolder,
        });
      }
    });

    const pdfArray = successfulDocs.map((d) => ({
      documentPath: d.documentPath,
      documentName: d.name,
      type: d.fileType,
      size: d.sizeBytes,
      documentId: d.documentId,
    }));

    // The API caps pdfArray at SAVE_BATCH_SIZE entries per call, so a large
    // successful-upload set (e.g. 70 files) is split into sequential
    // chunks (e.g. 50, then 20) — one saveList call at a time, waiting for
    // each to succeed before firing the next. The success modal only shows
    // once every batch has completed; any batch failing stops the sequence
    // and surfaces which batch failed, leaving the rest untouched so the
    // user can retry Save without re-uploading anything.
    const pdfBatches = chunkArray(pdfArray, SAVE_BATCH_SIZE);
    const totalBatches = pdfBatches.length;

    setIsSavingAll(true);

    const runBatch = (batchIndex) => {
      const isLastBatch = batchIndex === totalBatches - 1;

      saveList(
        {
          // title: title.trim(),
          folderArray,
          pdfArray: pdfBatches[batchIndex],
          // description: description.trim(),
        },
        {
          onSuccess: () => {
            if (isLastBatch) {
              setIsSavingAll(false);
              setShowSuccessModal(true);
            } else {
              runBatch(batchIndex + 1);
            }
          },
          onError: (err) => {
            setIsSavingAll(false);
            setGlobalError(
              err?.message ||
                (totalBatches > 1
                  ? `Failed to save documents (batch ${batchIndex + 1} of ${totalBatches}). Please try again.`
                  : 'Failed to save document list. Please try again.')
            );
          },
        }
      );
    };

    runBatch(0);
  };

  const handleDashboard = () => navigate('/');

  // const handleTitleChange = (e) => {
  //   setTitle(e.target.value);
  //   if (e.target.value.trim()) setErrors((prev) => ({ ...prev, title: undefined }));
  // };

  // const handleDescriptionChange = (e) => {
  //   if (e.target.value.length <= MAX_DESCRIPTION_LENGTH) {
  //     setDescription(e.target.value);
  //     if (e.target.value.trim()) setErrors((prev) => ({ ...prev, description: undefined }));
  //   }
  // };

  const handleCloseSuccessModal = () => {
    setShowSuccessModal(false);
    folderMapRef.current = {};
    // setFolderMap({});
    // setTitle('');
    // setDescription('');
    setDocuments([]);
    setErrors({});
    setGlobalError('');
    setDocLoadingState({});
    clearUploadQueue();
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  return (
    <UploadDocumentsWrapper>
      <UploadDocumentsContainer>
        <HeaderSection>
          <div>
            <SectionTitle>Upload Documents</SectionTitle>
            <SectionDescription>
              Upload files or folders to your document storage
            </SectionDescription>
          </div>
          <ActionButtons>
            <DashboardButton onClick={handleDashboard} disabled={buttonsDisabled} type="button">
              Dashboard
            </DashboardButton>
            <SaveButton
              onClick={handleSave}
              disabled={isSavingAll || buttonsDisabled || !hasSuccessfulUploads}
              type="button"
            >
              {isSavingAll ? (
                <Loader2
                  size={14}
                  className="spin"
                  style={{ animation: 'spin 1s linear infinite' }}
                />
              ) : null}
              {isSavingAll ? 'Saving…' : 'Save'}
            </SaveButton>
          </ActionButtons>
        </HeaderSection>

        {globalError && (
          <GlobalError role="alert">
            <AlertCircle size={15} style={{ flexShrink: 0 }} />
            {globalError}
          </GlobalError>
        )}

        <ContentGrid>
          {documents.length === 0 ? (
            <UploadSection>
              <SectionLabel>Upload Documents</SectionLabel>
              <DropZone onFilesSelect={handleFilesSelect} isUploading={false} />

              {errors.documents && (
                <ErrorText
                  role="alert"
                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                >
                  <AlertCircle size={13} />
                  {errors.documents}
                </ErrorText>
              )}
            </UploadSection>
          ) : (
            <UploadedDocumentsSection>
              <UploadedDocumentsHeaderRow>
                <SectionLabel style={{ margin: 0 }}>
                  Uploaded Documents
                  {documents.length > 0 && (
                    <span
                      style={{
                        marginLeft: 8,
                        fontWeight: 400,
                        color: 'var(--color-text-tertiary)',
                      }}
                    >
                      ({documents.length})
                    </span>
                  )}
                </SectionLabel>

                {/* One aggregate progress bar per real folder upload */}
                {Object.keys(folderGroups).length > 0 && (
                  <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', flex: '1 1 240px' }}>
                    {Object.entries(folderGroups).map(([rootFolder, docs]) => (
                      <FolderProgressBar key={rootFolder} rootFolder={rootFolder} docs={docs} />
                    ))}
                  </div>
                )}
              </UploadedDocumentsHeaderRow>

              {documents.length === 0 ? (
                <EmptyState>
                  <FileX
                    size={32}
                    strokeWidth={1.2}
                    style={{ color: 'var(--color-text-tertiary)' }}
                  />
                  <p style={{ margin: 0 }}>No documents uploaded yet</p>
                  <p style={{ margin: 0, fontSize: '12px' }}>
                    Upload files using the drop zone above
                  </p>
                </EmptyState>
              ) : (
                <DocumentsList role="list" aria-label="Uploaded documents">
                  {documents.map((doc) => (
                    <DocumentListItem
                      key={doc.id}
                      doc={doc}
                      onView={handleView}
                      onDelete={handleDelete}
                      onReupload={handleReupload}
                      onSkip={handleSkip}
                      isViewing={docLoadingState[doc.id] === 'viewing'}
                      isDeleting={docLoadingState[doc.id] === 'deleting'}
                      disabled={buttonsDisabled}
                    />
                  ))}
                </DocumentsList>
              )}
            </UploadedDocumentsSection>
          )}
        </ContentGrid>
      </UploadDocumentsContainer>

      <UploadSuccessModal isOpen={showSuccessModal} onClose={handleCloseSuccessModal} />
    </UploadDocumentsWrapper>
  );
};

export default UploadDocuments;
